import { ProductDuration } from '@essent/common';
import { validateNgrxState } from '@innogy/shared/forms';
import { validateSequential } from '@innogy/utils-deprecated';
import type { Action } from '@ngrx/store';
import { createReducer, on } from '@ngrx/store';
import {
  createFormGroupState,
  MarkAsSubmittedAction,
  onNgrxForms,
  onNgrxFormsAction,
  updateGroup,
  type FormState,
} from 'ngrx-forms';
import type { FormGroupState, ValidationErrors } from 'ngrx-forms/public_api';
import { required } from 'ngrx-forms/validation';

import { setPromoCodeId } from './affiliate-promo-code.actions';

export const PROMO_CODE_FORM_ID = 'promoCodeForm';
export const PROMO_CODE_INPUT_ID = `${PROMO_CODE_FORM_ID}.promoCode`;

type PromoCodePlan = {
  duration: ProductDuration;
  amount: number;
};

type PromoCodeFormState = {
  promoCode: string;
};

export interface AffiliatePromoCodeState {
  promoCodeId: string | null;
  promoCodePlans: PromoCodePlan[];
  formState: FormState<PromoCodeFormState>;
}

const initialState: AffiliatePromoCodeState = {
  promoCodeId: null,
  promoCodePlans: [],
  formState: createFormGroupState(PROMO_CODE_FORM_ID, {
    promoCode: '',
  }),
};

/**
 * DEBT: these promo codes are currently hardcoded (as with similar affiliate/partner integrations) and will be moved when the PoC has positive results.
 * @see https://jira.essent.nl/jira/browse/CTR-1877
 * @param promoCodeId
 * @returns state
 */
const getPromoCodePlansForId = (
  promoCodeId: string | null
): PromoCodePlan[] => {
  if (promoCodeId?.toLocaleLowerCase() === 'extrakorting') {
    return [
      { duration: ProductDuration.ONE, amount: 10 },
      { duration: ProductDuration.THREE, amount: 60 },
    ];
  }
  return [];
};

const updateStateWithPromoCodeId = (
  state: AffiliatePromoCodeState,
  promoCodeId: string | null
): AffiliatePromoCodeState => {
  const plans = getPromoCodePlansForId(promoCodeId);
  return {
    ...state,
    promoCodeId: plans.length ? promoCodeId : null,
    promoCodePlans: plans,
  };
};

const hasPromoCodePlanValidator = (promoCodeId: string): ValidationErrors => {
  if (!getPromoCodePlansForId(promoCodeId).length) {
    return {
      invalidCode: 'invalid promo code',
    };
  }
  return {};
};

const formSubmit = (
  state: AffiliatePromoCodeState
): AffiliatePromoCodeState => {
  let validatedState = validateNgrxState(state, validateForm);

  validatedState = updateStateWithPromoCodeId(
    validatedState,
    validatedState.formState.isValid
      ? validatedState.formState.value.promoCode
      : null
  );

  return validatedState;
};

const validateForm = (
  state: AffiliatePromoCodeState
): FormGroupState<PromoCodeFormState> =>
  updateGroup({
    promoCode: validateSequential(required, hasPromoCodePlanValidator),
  })(state.formState);

const _reducer = createReducer(
  initialState,
  on(setPromoCodeId, (state, { promoCodeId }) =>
    updateStateWithPromoCodeId(state, promoCodeId)
  ),
  onNgrxForms(),
  onNgrxFormsAction(MarkAsSubmittedAction, (state, action) =>
    action.controlId === PROMO_CODE_FORM_ID ? formSubmit(state) : state
  )
);

export function affiliatePromoCodeReducer(
  state: AffiliatePromoCodeState | undefined,
  action: Action
): AffiliatePromoCodeState {
  return _reducer(state, action);
}
